import { Injectable, Injector, inject } from '@angular/core';
import { BehaviorSubject, forkJoin, Observable } from 'rxjs';
import { ICommonAPIResponse } from '../../models/common-models';
import { API } from '../../constants/api-url';
import { HttpRequestService } from '../http-request/http-request.service';
import { DataGridFilterCondition } from '../../constants/datagrid-filter-condition.enum';
import { HelperService } from './helper.service';
import { INotificationMessageDescription, NotificationEntityType, NotificationUnion } from '../../models/notification.model';
import { AuthService } from '../auth/auth.service';
import { UserType } from '../../constants/user-type-enum';
import { IActionPermission } from '../../models/action-permission.model';
import { PERMISSION } from '../../constants/permission';
import { ToastrHelperService } from './toast-helper.service';
import { HttpContext } from '@angular/common/http';
import { ApprovalSubType } from '../../constants/preauth-request.enum';
import { ApprovalType } from '../../constants/apporval-type-enum';
import { Router } from '@angular/router';
import { PATH_CLAIM_BATCH, PATH_CLAIM_MY, PATH_PREAUTH_MY, PATH_PRESCRIPTION_MY, SLUG_VIEW_CLAIMS } from '../../constants/route-consts';

@Injectable({
  providedIn: 'root'
})
export class NotificationService {
  private httpRequestService = inject(HttpRequestService);
  private helperService = inject(HelperService);
  private toastrHelperService = inject(ToastrHelperService);
  private authService = inject(AuthService);
  private injector = inject(Injector);

  notifications$ = new BehaviorSubject<{ result: NotificationUnion[], trigger?: boolean }>({ result: [] });
  tpaNotifications$ = new BehaviorSubject<{ result: NotificationUnion[], trigger?: boolean }>({ result: [] });
  private hasPayerData = false;
  private hasProviderData = false;

  getPermission(type: number): IActionPermission {
    switch (type) {
      case UserType.Patient:
        return this.authService.getPageAccess(PERMISSION.PATIENT_NOTIFICATION.ALL);
      case UserType.Provider:
        return this.authService.getPageAccess(PERMISSION.PROVIDER_NOTIFICATION.ALL);
      case UserType.Payer:
        return this.authService.getPageAccess(PERMISSION.PAYER_NOTIFICATION.ALL);
      case UserType.TPA:
        return null;
      default: return {};
    }
  }

  getBaseUrl(): string | { payer: string; provider: string } {
    switch (this.loggedInUserType) {
      case UserType.Patient:
        return API.NOTIFICATION.PATIENT_NOTIFICATION;
      case UserType.Provider:
        return API.NOTIFICATION.PROVIDER_NOTIFICATION;
      case UserType.Payer:
        return API.NOTIFICATION.PAYER_NOTIFICATION;
      case UserType.TPA:
        return {
          payer: API.NOTIFICATION.PAYER_NOTIFICATION,
          provider: API.NOTIFICATION.PROVIDER_NOTIFICATION
        };
      default:
        throw new Error('Unsupported user type');
    }
  }

  getPrimaryKey(): string {
    switch (this.loggedInUserType) {
      case UserType.Patient:
        return 'patientNotificationId';
      case UserType.Provider:
        return 'providerNotificationId';
      case UserType.Payer:
        return 'payerNotificationId';
      case UserType.TPA:
        return 'description';
      default:
        throw new Error('Unsupported user type');
    }
  }

  getNotifications(): Observable<{ result: NotificationUnion[], trigger?: boolean }> {
    return this.notifications$.asObservable();
  }

  fetchNotifications(onlyUnread?: boolean, callback?: () => void, onDataForGrid?: (data) => void): void {
    const payload = this.getNotificationPayload(onlyUnread);
    if (this.loggedInUserType === UserType.TPA) {
      const context = !onlyUnread ? this.helperService.getSkipLoaderContext() : null;
      const requests = this.getRequestsForTPA('list', payload, context, 'list');

      if (!requests.length) {
        this.notifications$.next({ result: [] });
        this.toastrHelperService.showWarningMessage("COMMON.NOT_AUTHORIZED_FOR_RESOURCE");
        return;
      }

      forkJoin(requests).subscribe({
        next: (responses) => {
          this.hasPayerData = this.hasProviderData = false;
          responses.forEach(res => {
            if (res?.results?.length) {
              if (res.results.some(x => x['payerNotificationId']))
                this.hasPayerData = true;
              if (res.results.some(x => x['providerNotificationId']))
                this.hasProviderData = true;
            }
          });

          const combined = responses.flatMap(r => r?.results || []);
          const sortedNotification = combined.sort((a, b) => new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime());
          this.notifications$.next({ result: sortedNotification });
          this.tpaNotifications$.next({ result: sortedNotification });
          callback && callback();
          onDataForGrid && onDataForGrid(sortedNotification);
        },
        error: () => {
          callback && callback();
          onDataForGrid && onDataForGrid([]);
        }
      });
    } else {
      const url = `${this.getBaseUrl()}list`;
      this.httpRequestService.post(url, payload).subscribe({
        next: (response: ICommonAPIResponse<NotificationUnion>) => {
          this.notifications$.next({ result: response.results });
          callback && callback();
        },
        error: () => callback && callback()
      });
    }
  }

  addNotification(newNotification: NotificationUnion): void {
    const current = this.notifications$.getValue().result;
    this.notifications$.next({
      result: [newNotification, ...current]
    });

    this.tpaNotifications$.next({
      result: [newNotification, ...this.tpaNotifications$.getValue().result]
    });
    this.hasPayerData = this.hasPayerData || !!newNotification['payerNotificationId'];
    this.hasProviderData = this.hasProviderData || !!newNotification['providerNotificationId'];
  }

  markAllAsRead(payload?, callback?: () => void, skipLoader?: boolean, onlyUnread?: boolean): void {
    const context = skipLoader ? this.helperService.getSkipLoaderContext() : null;
    const payloadObj = payload ?? {
      searchCriteria: this.getNotificationPayload(onlyUnread),
      isAllSelected: true,
      includeIds: [],
      excludeIds: []
    };

    if (this.loggedInUserType === UserType.TPA) {
      const requests = this.getRequestsForTPA('markread', payloadObj, context, 'update', this.hasPayerData, this.hasProviderData);
      if (!requests.length) {
        this.toastrHelperService.showWarningMessage("COMMON.NOT_AUTHORIZED_FOR_RESOURCE");
        return;
      }

      forkJoin(requests).subscribe({
        next: () => this.fetchNotifications(false, callback),
        error: () => callback && callback()
      });
    } else {
      this.httpRequestService.put(`${this.getBaseUrl()}/markread`, payloadObj, null, null, context).subscribe({
        next: () => this.fetchNotifications(onlyUnread, callback),
        error: () => callback && callback()
      });
    }
  }

  markReadNotification(notification: NotificationUnion, gridRef?: { loadData: () => void }, trigger?: boolean, callback?: () => void, navigateToApproval?: boolean): void {
    let baseUrl: string;
    let primaryKey: string;
    const navigateToEntity = notification.navigateToEntity;
    notification.navigateToEntity = false;

    if (this.loggedInUserType === UserType.TPA) {
      if (notification['providerNotificationId']) {
        baseUrl = API.NOTIFICATION.PROVIDER_NOTIFICATION;
        primaryKey = 'providerNotificationId';
      } else if (notification['payerNotificationId']) {
        baseUrl = API.NOTIFICATION.PAYER_NOTIFICATION;
        primaryKey = 'payerNotificationId';
      }
      else return;
    } else {
      baseUrl = this.getBaseUrl() as string;
      primaryKey = this.getPrimaryKey();
    }

    const payload = {
      searchCriteria: null,
      isAllSelected: false,
      includeIds: [notification[primaryKey]],
      excludeIds: null
    };
    this.httpRequestService.put(`${baseUrl}/markread`, payload, null, null, this.helperService.getSkipLoaderContext()).subscribe({
      next: () => {
        gridRef?.loadData();
        const updated = this.notifications$.getValue().result.filter(notify => notify[this.getPrimaryKey()] !== notification[this.getPrimaryKey()]);
        this.notifications$.next({ result: updated, trigger });
        if (this.loggedInUserType === UserType.TPA) {
          const updatedGrid = this.tpaNotifications$.getValue().result.map(notify => {
            if (notify[primaryKey] === notification[primaryKey]) {
              return { ...notify, isRead: true };
            }
            return notify;
          });
          this.tpaNotifications$.next({ result: updatedGrid, trigger });
        }

        if (navigateToEntity && this.loggedInUserType !== UserType.Patient && navigateToApproval)
          this.redirectToEntityPage(notification, callback);
        else if (callback) callback();
      },
      error: () => callback && callback()
    });
  }

  formatNotificationMessage(description: string | INotificationMessageDescription): string {
    if (!description) return '';
    try {
      const descData = this.getNotificationDescData(description);
      if (descData)
        return this.helperService.isArabic() ? descData.descriptionArabic : descData.descriptionEnglish;
      else
        return description as string;
    } catch {
      return '';
    }
  }

  redirectToEntityPage(notification: NotificationUnion, callback?: () => void): void {
    const descData = this.getNotificationDescData(notification?.['description']);
    if (descData) {
      const entityTypeRaw = descData.entityType as unknown;
      let entityTypeText = typeof entityTypeRaw === 'string'
        ? entityTypeRaw
        : (typeof entityTypeRaw === 'number' ? (NotificationEntityType as unknown as Record<number, string>)[entityTypeRaw] : String(entityTypeRaw));

      // If the backend ever sends numeric values as strings, convert them to enum names.
      const maybeNumber = Number(entityTypeText);
      if (!Number.isNaN(maybeNumber))
        entityTypeText = (NotificationEntityType as unknown as Record<number, string>)[maybeNumber] ?? entityTypeText;

      const router = this.injector.get(Router);
      const entityTypeKey = entityTypeText?.trim().replace(/[\s_-]+/g, '');
      const myApprovalRoute = descData.approvalSubType == ApprovalSubType.Preauth ? PATH_PREAUTH_MY : descData.approvalSubType == ApprovalSubType.Claim ? PATH_CLAIM_MY : PATH_PRESCRIPTION_MY;
      const viewApprovalRoute = `${this.helperService.getRouteToViewPage(descData.approvalSubType?.toString() as ApprovalSubType)}/view/${descData.approvalRequestId}`;

      switch (entityTypeKey) {
        case 'CommunicationAdded':
        case 'CommunicationReplied':
          router.navigate([viewApprovalRoute], { state: { communicationId: descData.approvalCommunicationId } });
          break;
        case 'Adjudication':
        case 'ApprovalStatusChanged':
          router.navigate([viewApprovalRoute]);
          break;
        case 'HighCostDecisionMade':
          router.navigate([`${myApprovalRoute}/view/${descData.approvalRequestId}`], { state: { isFromNotification: true } });
          break;
        case 'HighCostReviewRequested':
          router.navigate([`${this.helperService.getRouteToMyApprovalPage(descData.approvalSubType.toString() as ApprovalSubType)}/review/${descData.approvalRequestId}`]);
          break;
        case 'BatchAssignment':
          router.navigate([`${PATH_CLAIM_BATCH}/${SLUG_VIEW_CLAIMS}/${descData.batchId}`]);
          break;
        case 'ApprovalAssignment':
          router.navigate([`${myApprovalRoute}/process/${descData.approvalRequestId}`]);
          break;
        default:
          break;
      }
    }
    callback && callback();
  }

  getNotificationKey(notification: NotificationUnion): string {
    return [
      notification['providerNotificationId'],
      notification['payerNotificationId'],
      notification['patientNotificationId'],
      notification.createdDate
    ].find(Boolean) as string || notification.createdDate;
  }

  getCommunicationDescription(notification: NotificationUnion): INotificationMessageDescription | null {
    const parsed = this.parseNotificationDescription(notification.description);
    if (!parsed) return null;

    const rawEntity = parsed.entityType as string | number;
    if (rawEntity == null) return null;

    const entityTypeValue = typeof rawEntity === 'number' ? rawEntity : Number(rawEntity);
    const entityTypeName = typeof rawEntity === 'string'
      ? rawEntity
      : NotificationEntityType[rawEntity];

    const isReplyCommunication = entityTypeValue === NotificationEntityType.CommunicationReplied || entityTypeName === 'CommunicationReplied';

    return isReplyCommunication ? parsed : null;
  }

  private getRequestsForTPA(type: string, payload, context: HttpContext, checkPermission: string, hasPayerData?: boolean, hasProviderData?: boolean) {
    const payerPermission = this.getPermission(UserType.Payer);
    const providerPermission = this.getPermission(UserType.Provider);

    const urls = {
      payer: `${API.NOTIFICATION.PAYER_NOTIFICATION}${type === 'list' ? `list` : `/markread`}`,
      provider: `${API.NOTIFICATION.PROVIDER_NOTIFICATION}${type === 'list' ? `list` : `/markread`}`
    };

    const requests: Observable<any>[] = [];
    if (payerPermission?.[checkPermission] && (hasPayerData ?? true))
      requests.push(this.httpRequestService[type === 'list' ? 'post' : 'put'](urls.payer, payload, null, null, context));

    if (providerPermission?.[checkPermission] && (hasProviderData ?? true))
      requests.push(this.httpRequestService[type === 'list' ? 'post' : 'put'](urls.provider, payload, null, null, context));

    return requests;
  }

  private isPlainObject(value: unknown): value is Record<string, unknown> {
    if (typeof value !== 'object' || value === null) return false;
    const proto = Object.getPrototypeOf(value);
    return proto === Object.prototype || proto === null;
  }

  private toCamelCaseKey(key: string): string {
    const trimmed = key?.trim();
    if (!trimmed) return trimmed;
    if (/[_\-\s]/.test(trimmed)) {
      const withoutSeparators = trimmed.replace(/[_\-\s]+(.)/g, (_, c: string) => c.toUpperCase());
      return withoutSeparators.replace(/^./, c => c.toLowerCase());
    }
    return trimmed.replace(/^./, c => c.toLowerCase());
  }

  private camelizeKeysDeep(value: unknown): unknown {
    if (Array.isArray(value)) return value.map(v => this.camelizeKeysDeep(v));
    if (!this.isPlainObject(value)) return value;

    return Object.entries(value).reduce<Record<string, unknown>>((acc, [key, val]) => {
      acc[this.toCamelCaseKey(key)] = this.camelizeKeysDeep(val);
      return acc;
    }, {});
  }

  private getNotificationDescData(description: unknown): INotificationMessageDescription | null {
    let parsed: unknown = description;

    // Parse only when it's a string and looks like valid JSON.
    if (typeof description === 'string') {
      try {
        parsed = JSON.parse(description);
        if (!this.isPlainObject(parsed)) return null;

        const camelized = this.camelizeKeysDeep(parsed) as Record<string, unknown>;

        const entityType = camelized['entityType'] as NotificationEntityType;
        const descriptionEnglish = camelized['descriptionEnglish'];
        const descriptionArabic = camelized['descriptionArabic'];
        const approvalType = camelized['approvalType'] as ApprovalType;
        const approvalSubType = camelized['approvalSubType'] as ApprovalSubType;
        const approvalRequestId = camelized['approvalRequestId'] as string;
        const approvalCommunicationId = camelized['approvalCommunicationId'] as string;
        const batchId = camelized['batchId'] as string;

        if (typeof entityType !== 'string' || typeof descriptionEnglish !== 'string' || typeof descriptionArabic !== 'string')
          return null;

        return {
          entityType,
          descriptionEnglish,
          descriptionArabic,
          approvalType,
          approvalSubType,
          approvalRequestId,
          approvalCommunicationId,
          batchId
        };
      } catch {
        return null;
      }
    } else
      return description as INotificationMessageDescription;
  }

  private getNotificationPayload(onlyUnread = true) {
    return {
      columns: [
        {
          name: "isRead",
          searchable: true,
          orderable: true,
          filterable: true,
          filterSearch: onlyUnread ? { condition: DataGridFilterCondition.IsEqualTo, value: "false" } : null,
          excelColumnName: "isRead"
        },
        {
          name: "createdDate",
          searchable: true,
          orderable: true,
          filterable: false,
          filterSearch: null,
          excelColumnName: "createdDate"
        }
      ],
      defaultSortingColumn: "createdDate",
      pageNumber: 0,
      pageSize: 0,
      sortBy: "createdDate",
      sortOrder: "desc",
      generalSearch: "",
      isSearchByCriteria: true
    };
  }

  private get loggedInUserType(): UserType {
    return this.authService.currentUserType();
  }

  private getDescriptionValue(parsed: Record<string, unknown>, ...keys: string[]): unknown {
    for (const key of keys) {
      if (Object.prototype.hasOwnProperty.call(parsed, key)) return parsed[key];
      const pascalKey = key.charAt(0).toUpperCase() + key.slice(1);
      if (Object.prototype.hasOwnProperty.call(parsed, pascalKey)) return parsed[pascalKey];
      const lowerKey = key.toLowerCase();
      const matchedKey = Object.keys(parsed).find(k => k.toLowerCase() === lowerKey);
      if (matchedKey) return parsed[matchedKey];
    }
    return undefined;
  }

  private parseNotificationDescription(description: unknown): INotificationMessageDescription | null {
    if (!description) return null;
    if (typeof description === 'object')
      return description as INotificationMessageDescription;

    if (typeof description === 'string') {
      try {
        const parsed = JSON.parse(description) as Record<string, unknown>;
        return {
          entityType: this.getDescriptionValue(parsed, 'entityType', 'entity_type') as NotificationEntityType,
          descriptionEnglish: String(this.getDescriptionValue(parsed, 'descriptionEnglish', 'description_english') ?? ''),
          descriptionArabic: String(this.getDescriptionValue(parsed, 'descriptionArabic', 'description_arabic') ?? ''),
          approvalRequestId: this.getDescriptionValue(parsed, 'approvalRequestId', 'approval_request_id') as string,
          approvalCommunicationId: this.getDescriptionValue(parsed, 'approvalCommunicationId', 'approval_communication_id') as string,
          approvalSubType: this.getDescriptionValue(parsed, 'approvalSubType', 'approval_sub_type') as ApprovalSubType,
          approvalType: this.getDescriptionValue(parsed, 'approvalType', 'approval_type') as ApprovalType,
          batchId: this.getDescriptionValue(parsed, 'batchId', 'batch_id') as string
        };
      } catch {
        return null;
      }
    }
    return null;
  }
}